import { Component, ElementRef, viewChild, input, effect, inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router, NavigationStart, Event } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-background-glitch',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div #backgroundImagesPreloadRef class="preloadImages"></div>
    <div class="bg-gray-500 glitch duotone">
      <div #backgroundImageRef1 class="glitch__item"></div>
      <div #backgroundImageRef2 class="glitch__item"></div>
      <div #backgroundImageRef3 class="glitch__item"></div>
      <div #backgroundImageRef4 class="glitch__item"></div>
      <div #backgroundImageRef5 class="glitch__item"></div>
    </div>
  `,
  styles: [`
    .preloadImages {
      display: none;
    }
  `]
})
export class BackgroundGlitchComponent {
  images = input<any[]>([]);
  
  readonly backgroundImagesPreloadRef = viewChild<ElementRef>('backgroundImagesPreloadRef');
  readonly backgroundImageRef1 = viewChild<ElementRef>('backgroundImageRef1');
  readonly backgroundImageRef2 = viewChild<ElementRef>('backgroundImageRef2');
  readonly backgroundImageRef3 = viewChild<ElementRef>('backgroundImageRef3');
  readonly backgroundImageRef4 = viewChild<ElementRef>('backgroundImageRef4');
  readonly backgroundImageRef5 = viewChild<ElementRef>('backgroundImageRef5');

  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);
  private currentBackgroundImage: any;

  constructor() {
    this.router.events.pipe(takeUntilDestroyed()).subscribe((event: Event) => {
      if (event instanceof NavigationStart && this.images().length) {
        this.backgroundImageSwitcher(event.url);
      }
    });

    effect(() => {
      const imgs = this.images();
      if (imgs.length > 0) {
        this.backgroundImageSwitcher(this.router.url);
      }
    });
  }

  private backgroundImageSwitcher(url: string): void {
    if (!isPlatformBrowser(this.platformId) || typeof window === 'undefined') {
      return;
    }

    const images = this.images();
    if (!images.length) return;

    const backgroundImageRef1 = this.backgroundImageRef1();
    if (!backgroundImageRef1) return;

    if (url === '/about') {
      this.currentBackgroundImage = images[1] || images[0];
    } else if (url === '/coding') {
      this.currentBackgroundImage = images[2] || images[0];
    } else if (url === '/writing') {
      this.currentBackgroundImage = images[3] || images[0];
    } else if (url === '/music') {
      this.currentBackgroundImage = images[4] || images[0];
    } else {
      this.currentBackgroundImage = images[0];
    }

    const preloadRef = this.backgroundImagesPreloadRef();
    if (preloadRef) {
      if (window.innerWidth >= 769) {
        preloadRef.nativeElement.style.setProperty('--bgImageDesktop', images.map(img => img.desktop).join(','));
      } else {
        preloadRef.nativeElement.style.setProperty('--bgImageMobile', images.map(img => img.tablet || img.mobile).join(','));
      }
    }

    const refs = [
      this.backgroundImageRef1(),
      this.backgroundImageRef2(),
      this.backgroundImageRef3(),
      this.backgroundImageRef4(),
      this.backgroundImageRef5()
    ];

    refs.forEach(ref => {
      if (ref) {
        ref.nativeElement.style.setProperty('--bgImageMobile', this.currentBackgroundImage.mobile);
        ref.nativeElement.style.setProperty('--bgImageTablet', this.currentBackgroundImage.tablet);
        ref.nativeElement.style.setProperty('--bgImageDesktop', this.currentBackgroundImage.desktop);
      }
    });
  }
}
